/**
 * WaChatComposer — Barra de entrada de mensajes estilo WhatsApp Web.
 *
 * Funcionalidades:
 * - Texto con envío por Enter (Shift+Enter para nueva línea)
 * - Modo nota interna (fondo amarillo, solo visible para agentes)
 * - Emojis popup (sin dependencia externa)
 * - Adjuntar archivos (imagen/video/documento)
 * - Grabación de audio
 * - Respuestas rápidas (/ + shortcut)
 * - Búsqueda en base de conocimiento
 * - Copilot IA suggestion
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Text,
  TextField,
  Spinner,
  InlineStack,
  BlockStack,
  Icon,
  Banner,
  Button,
  Popover,
  DatePicker,
  Box,
  Modal,
} from '@/lib/polaris';
import {
  NoteIcon,
  ImageIcon,
  FileIcon,
  SmileyHappyIcon,
  AttachmentIcon,
  ClockIcon,
  MicrophoneIcon,
  SendIcon,
  XSmallIcon,
  BookOpenIcon,
  PlayIcon,
  PauseCircleIcon,
  DeleteIcon,
  PlusCircleIcon,
  ProductIcon,
} from '@/lib/polaris-icons';
import { WaKbSearchPopover } from '@/components/whatsapp/WaKbSearchPopover';
import { WaCopilotSuggestion, SUGERENCIA_DE_IA_ACTIVA } from '@/components/whatsapp/WaCopilotSuggestion';
import { WaComposerAiTools } from '@/components/whatsapp/WaComposerAiTools';
import { WaComposerFormattedPreview } from '@/components/whatsapp/WaComposerFormattedPreview';
import { useWaAudioRecorder } from '@/hooks/useWaAudioRecorder';
import { prewarmWaSend } from '@/lib/waPrewarm';

// ─── Types ───

interface QuickReply {
  id: string;
  shortcut: string;
  title: string;
  body: string;
  media_url?: string | null;
  media_type?: string | null;
}

// ─── Media validation ───
// validateMediaFile + constantes extraídas a _helpers/composerValidation.ts
// para cobertura de tests sin DOM/File mocks.
import { validateMediaFile } from './_helpers/composerValidation';
import { waMediaPreview } from './wa-helpers';

// ─── Emoji data ───

const EMOJI_CATEGORIES = [
  { name: 'Caras', emojis: ['😀','😂','🤣','😍','🥰','😘','😊','😁','😆','🤗','🤔','😅','😉','😎','🥺','😢','😭','😤','😡','🤯','😱','🤮','🤢','🥳','🤩','😴','🙄','😬','🤫','🤭','😈','👻','💀','🤡'] },
  { name: 'Manos', emojis: ['👍','👎','👋','🤝','🙏','💪','👏','🤟','✌️','🤞','👌','🫡','🫶','❤️','🔥','⭐','✨','💯','🎉','🎊'] },
  { name: 'Objetos', emojis: ['📱','💻','📷','🎵','🎤','📞','💰','🛒','🍕','🍔','☕','🎂','🚗','✈️','🏠','⏰','📍','🔗','📎','✅','❌','⚠️','💡','🔔'] },
];

// ─── Reply context ───

export interface ReplyContext {
  id: string;
  body: string | null;
  msg_type: string;
  direction: string;
  media_url?: string | null;
  media_caption?: string | null;
}

// ─── Props ───

/** Miembro del tenant disponible para @mention dentro de notas internas. */
export interface ComposerStaffMember {
  id: string;
  name: string;
}

interface Props {
  tenantId: string | null;
  conversationId: string | null;
  conversationStatus: string | null;
  quickReplies: QuickReply[];
  lastInboundMessage: string | null;
  replyingTo?: ReplyContext | null;
  onCancelReply?: () => void;
  /**
   * Envía texto. `mentionedUserIds` solo viene poblado cuando `isNote=true` y
   * el agente etiquetó a alguien con @ desde el autocomplete.
   */
  onSend: (text: string, isNote: boolean, mentionedUserIds?: string[]) => void;
  /**
   * `isNote`: adjuntar en modo "Nota interna" no debe salir al cliente.
   * `caption`: pie de foto — viaja en el MISMO mensaje que el archivo.
   */
  onSendMedia: (file: File, type: 'image' | 'video' | 'document' | 'audio', audioDuration?: number, isNote?: boolean, caption?: string) => void;
  onSendMediaUrl?: (url: string, type: string, caption?: string) => void;
  onTyping?: () => void;
  onScheduleMessage?: (text: string, scheduledAt: string) => void;
  onAddQuickReply?: () => void;
  onOpenProductPicker?: () => void;
  /** Staff del tenant para autocomplete @ en notas internas. Vacío = no hay autocomplete. */
  tenantStaff?: ComposerStaffMember[];
}

// ─── Component ───

export function WaChatComposer({
  tenantId,
  conversationId,
  conversationStatus,
  quickReplies,
  lastInboundMessage,
  replyingTo,
  onCancelReply,
  onSend,
  onSendMedia,
  onTyping,
  onSendMediaUrl,
  onScheduleMessage,
  onAddQuickReply,
  onOpenProductPicker,
  tenantStaff = [],
}: Props) {
  const [newMessage, setNewMessage] = useState('');
  const [pendingMedia, setPendingMedia] = useState<{ url: string; type: string } | null>(null);
  // Archivo pegado del portapapeles: pendiente de envío hasta que el usuario
  // confirme con Enter / botón send. Se renderiza en preview (igual UX que el
  // pegado de WhatsApp Web). El blob URL local da preview inmediato sin
  // esperar upload; cuando el agente confirma, onSendMedia lo sube.
  const [pendingPasteFile, setPendingPasteFile] = useState<{
    file: File;
    blobUrl: string;
    type: 'image' | 'video' | 'document';
    /** 'paste' = portapapeles · 'picker' = botón adjuntar. Solo cambia el copy. */
    source: 'paste' | 'picker';
  } | null>(null);
  /** Confirmación al descartar el adjunto (mismo patrón que WhatsApp). */
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [isNoteMode, setIsNoteMode] = useState(false);
  // Autocomplete @mention (solo activo en isNoteMode).
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const [mentionAtPos, setMentionAtPos] = useState<number>(0);
  const [mentionSelectedIdx, setMentionSelectedIdx] = useState(0);
  /** Coords (viewport) del dropdown — calculadas desde el textarea para
   *  renderizar vía portal y escapar cualquier overflow/stacking del composer. */
  const [mentionAnchor, setMentionAnchor] = useState<{ left: number; bottom: number } | null>(null);
  /** IDs de staff etiquetados vía autocomplete en este mensaje. Se filtra
   *  contra el body final al enviar (si el user borró un @nombre, se descarta). */
  const [mentionedUserIds, setMentionedUserIds] = useState<Set<string>>(new Set());
  /** Map name → userId para resolver @nombres al enviar (lookup de display name). */
  const mentionedNamesRef = useRef<Map<string, string>>(new Map());
  const [sendError, setSendError] = useState<string | null>(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showAttachMenu, setShowAttachMenu] = useState(false);
  const [showKbSearch, setShowKbSearch] = useState(false);
  const [showQuickReplies, setShowQuickReplies] = useState(false);
  const [qrFilter, setQrFilter] = useState('');
  const [qrSelectedIdx, setQrSelectedIdx] = useState(0);
  const {
    isRecording, isPaused, isPlayingPreview, recordingTime,
    startRecording, pauseRecording, resumeRecording,
    playPreview, stopPreview, sendRecording, cancelRecording,
  } = useWaAudioRecorder({ onSendMedia, onError: setSendError });
  const [sending, setSending] = useState(false);
  const [showSchedulePicker, setShowSchedulePicker] = useState(false);
  const [scheduleMonth, setScheduleMonth] = useState(() => new Date().getMonth());
  const [scheduleYear, setScheduleYear] = useState(() => new Date().getFullYear());
  const [scheduleSelectedDate, setScheduleSelectedDate] = useState<Date | null>(null);
  const [scheduleTime, setScheduleTime] = useState('');

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const fileAcceptRef = useRef('image/*');
  const lastTypingEmitRef = useRef(0);

  // Close popups on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('[data-wa-popup]') && !target.closest('[data-wa-trigger]')) {
        setShowEmojiPicker(false);
        setShowAttachMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Auto-resize native textarea
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = '20px';
    el.style.height = `${Math.min(el.scrollHeight, 140)}px`;
    el.style.overflowY = el.scrollHeight > 140 ? 'auto' : 'hidden';
  }, [newMessage]);

  // Focus textarea when replying
  useEffect(() => {
    if (replyingTo) textareaRef.current?.focus();
  }, [replyingTo]);

  // Reset state when conversation changes
  useEffect(() => {
    setNewMessage('');
    setPendingMedia(null);
    setIsNoteMode(false);
    setSendError(null);
    setShowEmojiPicker(false);
    setShowAttachMenu(false);
    setShowKbSearch(false);
    setShowQuickReplies(false);
    setMentionQuery(null);
    setMentionedUserIds(new Set());
    mentionedNamesRef.current = new Map();
  }, [conversationId]);

  // ─── @mention autocomplete ───
  // Recalcula coords del dropdown cuando se abre o cambia la query (el textarea
  // crece al escribir, por lo que su top puede subir y necesitamos refrescar).
  useEffect(() => {
    if (mentionQuery === null) {
      setMentionAnchor(null);
      return;
    }
    const el = textareaRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setMentionAnchor({
      left: rect.left,
      // bottom = distancia desde el bottom del viewport hasta donde queremos
      // que termine el dropdown (justo encima del textarea).
      bottom: window.innerHeight - rect.top + 6,
    });
  }, [mentionQuery, newMessage]);

  // Filtramos staff por la query actual. Match case-insensitive sobre el nombre.
  const filteredMentionStaff = useMemo(() => {
    if (mentionQuery === null || tenantStaff.length === 0) return [];
    const q = mentionQuery.toLowerCase();
    if (!q) return tenantStaff.slice(0, 8); // primeros 8 cuando aún no escribió nada
    return tenantStaff.filter((s) => s.name.toLowerCase().includes(q)).slice(0, 8);
  }, [mentionQuery, tenantStaff]);

  // Detecta si el cursor está dentro de una referencia tipo `@query` (sin espacios
  // entre el `@` y el cursor). Devuelve null si no aplica.
  const detectMentionContext = useCallback((text: string, cursorPos: number) => {
    let i = cursorPos - 1;
    while (i >= 0 && text[i] !== '@' && !/\s/.test(text[i])) i--;
    if (i < 0 || text[i] !== '@') return null;
    // Evitar disparar en emails: si el char antes del @ es alfanumérico, ignorar.
    if (i > 0 && /[A-Za-z0-9]/.test(text[i - 1])) return null;
    return { query: text.slice(i + 1, cursorPos), atPos: i };
  }, []);

  // Inserta `@FullName ` reemplazando desde @ hasta el cursor actual.
  const insertMention = useCallback((member: ComposerStaffMember) => {
    const el = textareaRef.current;
    if (!el) return;
    const cursor = el.selectionStart ?? newMessage.length;
    const before = newMessage.slice(0, mentionAtPos);
    const after = newMessage.slice(cursor);
    const inserted = `@${member.name} `;
    const next = before + inserted + after;
    setNewMessage(next);
    // Track ID + name lookup. Si dos staff comparten nombre solo se nota uno;
    // ese caso es raro y el backend tolera duplicados (UNIQUE no aplica acá).
    setMentionedUserIds((prev) => new Set(prev).add(member.id));
    mentionedNamesRef.current.set(member.name.toLowerCase(), member.id);
    setMentionQuery(null);
    setMentionSelectedIdx(0);
    // Reposicionar cursor justo después del @nombre insertado.
    queueMicrotask(() => {
      const newPos = (before + inserted).length;
      el.focus();
      el.setSelectionRange(newPos, newPos);
    });
  }, [newMessage, mentionAtPos]);

  // Quick reply filtering
  const filteredQR = useMemo(() => {
    if (!qrFilter) return quickReplies;
    const q = qrFilter.toLowerCase();
    return quickReplies.filter((qr) =>
      qr.shortcut.toLowerCase().includes(q) ||
      qr.title.toLowerCase().includes(q) ||
      qr.body.toLowerCase().includes(q)
    );
  }, [quickReplies, qrFilter]);

  // ─── Send handler (con debounce para evitar doble-click) ───
  const handleSend = useCallback(() => {
    if (sending) return;
    if (!newMessage.trim() && !pendingMedia && !pendingPasteFile) return;
    const text = newMessage.trim();
    const sendingNote = isNoteMode;
    setNewMessage('');
    if (sendingNote) setIsNoteMode(false);
    setSendError(null);
    setSending(true);
    if (pendingPasteFile) {
      // El texto del composer viaja como PIE DE FOTO del archivo (un solo
      // mensaje, como WhatsApp). Antes se mandaba como mensaje aparte después
      // del media: llegaban dos globos y el cliente perdía la relación.
      onSendMedia(pendingPasteFile.file, pendingPasteFile.type, undefined, sendingNote, text || undefined);
      URL.revokeObjectURL(pendingPasteFile.blobUrl);
      setPendingPasteFile(null);
    } else if (pendingMedia && onSendMediaUrl) {
      onSendMediaUrl(pendingMedia.url, pendingMedia.type, text || undefined);
      setPendingMedia(null);
    } else if (text) {
      // Resolvemos qué IDs siguen vigentes en el body final. Esto evita enviar
      // menciones cuyo @nombre fue borrado tras seleccionarlo del autocomplete.
      let mentionedIds: string[] | undefined;
      if (sendingNote && mentionedUserIds.size > 0) {
        const lowered = text.toLowerCase();
        const stillPresent: string[] = [];
        for (const [name, uid] of mentionedNamesRef.current) {
          if (lowered.includes(`@${name}`) && mentionedUserIds.has(uid)) {
            stillPresent.push(uid);
          }
        }
        if (stillPresent.length > 0) mentionedIds = stillPresent;
      }
      onSend(text, sendingNote, mentionedIds);
      // Limpiar tracking para el próximo mensaje.
      setMentionedUserIds(new Set());
      mentionedNamesRef.current = new Map();
    }
    if (replyingTo) onCancelReply?.();
    // Desbloqueo inmediato: la UI ya limpió el input (setNewMessage('')) y el
    // mensaje optimista ya se renderizó. El usuario puede escribir el siguiente
    // mensaje sin esperar. La protección anti-doble-envío real vive en el
    // backend (client_idempotency_key + UNIQUE en wa_messages).
    setSending(false);
  }, [sending, newMessage, isNoteMode, pendingMedia, pendingPasteFile, onSend, onSendMedia, onSendMediaUrl, replyingTo, onCancelReply, mentionedUserIds]);

  // ─── File handler ───
  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const validation = validateMediaFile(file);
    if (!validation.valid) {
      setSendError(validation.error ?? 'Archivo no válido');
      e.target.value = '';
      return;
    }
    const mime = file.type || '';
    const type: 'image' | 'video' | 'document' = mime.startsWith('image/') ? 'image'
      : mime.startsWith('video/') ? 'video' : 'document';
    // Antes se enviaba AL TOQUE, sin preview ni posibilidad de escribir un pie
    // de foto. Ahora pasa por el mismo preview que el pegado (patrón WhatsApp):
    // se ve la imagen y podés escribir el texto debajo antes de enviar.
    setPendingPasteFile({ file, blobUrl: URL.createObjectURL(file), type, source: 'picker' });
    e.target.value = '';
  }, []);

  // ─── Paste handler — pegar imagen/video/archivo desde portapapeles ───
  // Patrón WhatsApp Web: el archivo se pre-carga en el composer como
  // preview, NO se envía inmediato. El agente puede agregar caption,
  // confirmar con Enter / botón send, o descartar con la X.
  const handlePaste = useCallback((e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const items = e.clipboardData?.items;
    if (!items || items.length === 0) return;
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.kind !== 'file') continue;
      const file = item.getAsFile();
      if (!file) continue;
      const mime = file.type || '';
      const type: 'image' | 'video' | 'document' = mime.startsWith('image/') ? 'image'
        : mime.startsWith('video/') ? 'video' : 'document';
      const validation = validateMediaFile(file);
      if (!validation.valid) {
        setSendError(validation.error ?? 'Archivo del portapapeles no válido');
        e.preventDefault();
        return;
      }
      // Limpiar el preview anterior si había uno (revocar blob URL).
      setPendingPasteFile((prev) => {
        if (prev) URL.revokeObjectURL(prev.blobUrl);
        return { file, blobUrl: URL.createObjectURL(file), type, source: 'paste' as const };
      });
      e.preventDefault();
      return; // un solo archivo por paste
    }
    // Sin archivos en clipboard → comportamiento default (texto).
  }, []);

  // Limpia el blob URL al desmontar para no leakear memoria.
  useEffect(() => {
    return () => {
      if (pendingPasteFile) URL.revokeObjectURL(pendingPasteFile.blobUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {/*
        Sugerencia de IA — apagada. La bandera se consulta ACÁ y no dentro del componente porque lo que
        hay que evitar es la LLAMADA a `wa-copilot` que demora la apertura del chat; un componente
        montado que se oculta a sí mismo ya pagó la espera. Ver SUGERENCIA_DE_IA_ACTIVA.
      */}
      {SUGERENCIA_DE_IA_ACTIVA && conversationId && tenantId ? (
        <WaCopilotSuggestion
          tenantId={tenantId}
          conversationId={conversationId}
          lastInboundMessage={lastInboundMessage}
          onAccept={(text) => setNewMessage(text)}
          visible={!isNoteMode && conversationStatus !== 'resolved'}
        />
      ) : null}

      {/* Composer bar — floating over chat background like WhatsApp Web */}
      <div style={{
        padding: '0 10px 12px 10px',
        position: 'relative',
      }}>
        {sendError ? (
          <div style={{ marginBottom: 8 }}>
            <Banner tone="critical" onDismiss={() => setSendError(null)}>
              <p>{sendError}</p>
            </Banner>
          </div>
        ) : null}

        {/* Pending media preview from quick reply */}
        {pendingMedia && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6,
            padding: '6px 10px', background: '#fff', borderRadius: 10,
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          }}>
            {pendingMedia.type === 'image' ? (
              <img src={pendingMedia.url} alt="" style={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 6 }} />
            ) : pendingMedia.type === 'video' ? (
              <div style={{ width: 44, height: 44, borderRadius: 6, background: '#e0e0e0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span style={{ fontSize: 20 }}>🎥</span>
              </div>
            ) : (
              <div style={{ width: 44, height: 44, borderRadius: 6, background: '#e0e0e0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Icon source={FileIcon} tone="subdued" />
              </div>
            )}
            <span style={{ flex: 1, fontSize: 13, color: '#3b4a54' }}>
              {pendingMedia.type === 'image' ? 'Imagen adjunta' : pendingMedia.type === 'video' ? 'Video adjunto' : 'Documento adjunto'}
            </span>
            <button
              type="button"
              onClick={() => setPendingMedia(null)}
              className="wa-icon-btn"
              title="Quitar adjunto"
            >
              <span style={{ color: '#ea4335' }}><Icon source={XSmallIcon} /></span>
            </button>
          </div>
        )}

        {/* Preview de archivo pegado del portapapeles. El blob URL local
            renderiza al toque sin esperar upload. Botón X descarta. */}
        {pendingPasteFile && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6,
            padding: '6px 10px', background: '#fff', borderRadius: 10,
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          }}>
            {pendingPasteFile.type === 'image' ? (
              <img
                src={pendingPasteFile.blobUrl}
                alt=""
                style={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 6 }}
              />
            ) : pendingPasteFile.type === 'video' ? (
              <video
                src={pendingPasteFile.blobUrl}
                style={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 6 }}
              />
            ) : (
              <div style={{ width: 44, height: 44, borderRadius: 6, background: '#e0e0e0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Icon source={FileIcon} tone="subdued" />
              </div>
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, color: '#3b4a54', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {pendingPasteFile.file.name || (pendingPasteFile.type === 'image' ? 'Imagen pegada' : pendingPasteFile.type === 'video' ? 'Video pegado' : 'Archivo pegado')}
              </div>
              <div style={{ fontSize: 11, color: '#8696a0' }}>
                {(pendingPasteFile.file.size / 1024).toFixed(0)} KB · {pendingPasteFile.source === 'paste' ? 'pegado del portapapeles' : 'escribí un pie de foto abajo (opcional)'}
              </div>
            </div>
            <button
              type="button"
              onClick={() => setConfirmDiscard(true)}
              className="wa-icon-btn"
              title="Quitar adjunto"
            >
              <span style={{ color: '#ea4335' }}><Icon source={XSmallIcon} /></span>
            </button>
          </div>
        )}

        {/* Confirmación al descartar el adjunto (igual que WhatsApp: la X no
            tira el archivo de una — se puede haber elegido por error). */}
        {confirmDiscard && pendingPasteFile && (
          <Modal
            open
            onClose={() => setConfirmDiscard(false)}
            title="¿Quieres descartar la selección?"
            primaryAction={{
              content: 'Descartar',
              destructive: true,
              onAction: () => {
                URL.revokeObjectURL(pendingPasteFile.blobUrl);
                setPendingPasteFile(null);
                setConfirmDiscard(false);
              },
            }}
            secondaryActions={[{ content: 'Cancelar', onAction: () => setConfirmDiscard(false) }]}
          >
            <Modal.Section>
              <Text as="p" variant="bodyMd" tone="subdued">
                Se va a quitar el archivo adjunto y el pie de foto que hayas escrito.
              </Text>
            </Modal.Section>
          </Modal>
        )}

        {/* Emoji picker popup */}
        {showEmojiPicker && (
          <div data-wa-popup style={{
            position: 'absolute', bottom: '100%', left: 16, width: 320, maxHeight: 280,
            background: 'var(--p-color-bg-surface)', border: '1px solid var(--p-color-border)',
            borderRadius: 12, boxShadow: '0 -4px 20px rgba(0,0,0,0.15)', marginBottom: 4,
            zIndex: 20, overflowY: 'auto', padding: '8px 10px',
          }}>
            {EMOJI_CATEGORIES.map((cat) => (
              <div key={cat.name} style={{ marginBottom: 8 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--p-color-text-subdued)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>{cat.name}</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  {cat.emojis.map((emoji) => (
                    <button
                      key={emoji}
                      type="button"
                      onClick={() => { setNewMessage((prev) => prev + emoji); setShowEmojiPicker(false); }}
                      style={{
                        width: 34, height: 34, fontSize: 20, border: 'none', background: 'transparent',
                        borderRadius: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        transition: 'background 0.15s',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--p-color-bg-surface-hover)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* KB search popover */}
        {showKbSearch && tenantId ? (
          <WaKbSearchPopover
            tenantId={tenantId}
            onSelectArticle={(text) => {
              setNewMessage(text);
              setShowKbSearch(false);
            }}
            onClose={() => setShowKbSearch(false)}
          />
        ) : null}

        {/* Attachment menu popup */}
        {showAttachMenu && (
          <div data-wa-popup style={{
            position: 'absolute', bottom: '100%', left: 16, width: 200,
            background: 'var(--p-color-bg-surface)', border: '1px solid var(--p-color-border)',
            borderRadius: 10, boxShadow: '0 4px 24px rgba(0,0,0,0.12)', marginBottom: 4,
            zIndex: 20, overflow: 'hidden', padding: '4px 0',
          }}>
            {[
              { label: 'Fotos y videos', accept: 'image/*,video/*', type: 'image' as const, icon: (
                <span style={{ color: '#00a884' }}><Icon source={ImageIcon} /></span>
              )},
              { label: 'Documento', accept: '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip,.rar', type: 'document' as const, icon: (
                <span style={{ color: '#00a884' }}><Icon source={FileIcon} /></span>
              )},
            ].map((opt) => (
              <button
                key={opt.type}
                type="button"
                onClick={() => {
                  fileAcceptRef.current = opt.accept;
                  setShowAttachMenu(false);
                  setTimeout(() => fileInputRef.current?.click(), 50);
                }}
                style={{
                  width: '100%', padding: '10px 16px', border: 'none',
                  background: 'transparent', textAlign: 'left', cursor: 'pointer',
                  fontSize: 13, fontWeight: 500, color: 'var(--p-color-text)',
                  display: 'flex', alignItems: 'center', gap: 12,
                  borderBottom: '1px solid var(--p-color-border-subdued)',
                  transition: 'background 0.15s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--p-color-bg-surface-hover)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              >
                {opt.icon}
                <span>{opt.label}</span>
              </button>
            ))}
            {onOpenProductPicker && (
              <button
                type="button"
                onClick={() => { setShowAttachMenu(false); onOpenProductPicker(); }}
                style={{
                  width: '100%', padding: '10px 16px', border: 'none',
                  background: 'transparent', textAlign: 'left', cursor: 'pointer',
                  fontSize: 13, fontWeight: 500, color: 'var(--p-color-text)',
                  display: 'flex', alignItems: 'center', gap: 12,
                  transition: 'background 0.15s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--p-color-bg-surface-hover)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              >
                <span style={{ color: '#00a884' }}><Icon source={ProductIcon} /></span>
                <span>Productos</span>
              </button>
            )}
          </div>
        )}

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          style={{ display: 'none' }}
          accept={fileAcceptRef.current}
          onChange={handleFileSelect}
        />

        {/* Main input bar — always uses pill layout */}
        <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end', position: 'relative' }}>
            {/* Quick replies dropdown — fuera del pill container para escapar su overflow:hidden */}
            {showQuickReplies ? (
              <div style={{
                position: 'absolute', bottom: '100%', left: 0, right: 0,
                maxHeight: 260, overflowY: 'auto',
                background: 'var(--p-color-bg-surface)', border: '1px solid var(--p-color-border)',
                borderRadius: 10, boxShadow: '0 -4px 16px rgba(0,0,0,0.12)',
                marginBottom: 8, zIndex: 30,
              }}>
                {filteredQR.length > 0 ? filteredQR.map((qr, idx) => (
                  <div
                    key={qr.id}
                    onClick={() => {
                      setShowQuickReplies(false);
                      setQrFilter('');
                      setNewMessage(qr.body);
                      if (qr.media_url && qr.media_type) {
                        setPendingMedia({ url: qr.media_url, type: qr.media_type });
                      } else {
                        setPendingMedia(null);
                      }
                    }}
                    onMouseEnter={() => setQrSelectedIdx(idx)}
                    style={{
                      padding: '8px 14px', cursor: 'pointer',
                      background: idx === qrSelectedIdx ? 'var(--p-color-bg-surface-hover)' : 'transparent',
                      borderBottom: '1px solid var(--p-color-border-subdued)',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                      <span style={{ fontWeight: 600, fontSize: 13, color: '#25D366' }}>{qr.shortcut}</span>
                      <span style={{ fontSize: 13, color: 'var(--p-color-text-subdued)' }}>{qr.title}</span>
                      {qr.media_url && (
                        <span style={{ color: '#8696a0', display: 'flex', transform: 'scale(0.75)' }}>
                          <Icon source={qr.media_type === 'image' ? ImageIcon : qr.media_type === 'video' ? ImageIcon : FileIcon} />
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--p-color-text-subdued)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {qr.body || (qr.media_type === 'image' ? '📷 Imagen' : qr.media_type === 'video' ? '🎥 Video' : '📎 Documento')}
                    </div>
                  </div>
                )) : (
                  <div style={{ padding: '12px 14px', textAlign: 'center' }}>
                    <span style={{ fontSize: 13, color: 'var(--p-color-text-subdued)' }}>
                      {quickReplies.length === 0 ? 'No hay respuestas rápidas' : 'Sin resultados'}
                    </span>
                  </div>
                )}
                {onAddQuickReply ? (
                  <div
                    onClick={() => {
                      setShowQuickReplies(false);
                      setQrFilter('');
                      onAddQuickReply();
                    }}
                    style={{
                      padding: '10px 14px', cursor: 'pointer',
                      borderTop: '1px solid var(--p-color-border-subdued)',
                      display: 'flex', alignItems: 'center', gap: 8,
                      transition: 'background 0.15s',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--p-color-bg-surface-hover)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    <span style={{ color: '#00a884' }}><Icon source={PlusCircleIcon} /></span>
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#00a884' }}>Agregar respuesta rápida</span>
                  </div>
                ) : null}
              </div>
            ) : null}
            {/* Pill-shaped input container like WhatsApp Web */}
            <div style={{
              flex: 1,
              display: 'flex', flexDirection: 'column',
              background: isNoteMode ? '#FFF9C4' : '#fff',
              border: `1px solid ${isNoteMode ? '#FBC02D' : 'transparent'}`,
              borderRadius: 21,
              minHeight: 48,
              position: 'relative',
              boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
              overflow: 'hidden',
            }}>
              {/* Reply bar — WhatsApp Web style */}
              {replyingTo && !isRecording ? (
                <div style={{
                  display: 'flex', alignItems: 'stretch',
                  margin: '6px 6px 0 6px',
                  borderRadius: 10,
                  background: '#f0f2f5',
                  overflow: 'hidden',
                  minHeight: 50,
                }}>
                  {/* Green left bar */}
                  <div style={{
                    width: 4, flexShrink: 0,
                    background: replyingTo.direction === 'inbound' ? '#06cf9c' : '#53bdeb',
                    borderRadius: '10px 0 0 10px',
                  }} />
                  {/* Content */}
                  <div style={{ flex: 1, padding: '6px 0 6px 10px', minWidth: 0 }}>
                    <div style={{
                      fontSize: 12, fontWeight: 600,
                      color: replyingTo.direction === 'inbound' ? '#06cf9c' : '#53bdeb',
                      marginBottom: 2,
                    }}>
                      {replyingTo.direction === 'inbound' ? 'Contacto' : 'Tú'}
                    </div>
                    <div style={{
                      fontSize: 13, color: '#667781',
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                      maxWidth: '100%',
                    }}>
                      {waMediaPreview(replyingTo.msg_type, replyingTo.media_caption) || (replyingTo.body?.slice(0, 120) ?? '')}
                    </div>
                  </div>
                  {/* Thumbnail for images */}
                  {replyingTo.media_url && replyingTo.msg_type === 'image' ? (
                    <img
                      src={replyingTo.media_url}
                      alt=""
                      style={{
                        width: 56, height: 56, objectFit: 'cover',
                        borderRadius: '0 10px 10px 0', flexShrink: 0,
                      }}
                    />
                  ) : null}
                  {/* Close button */}
                  <button
                    type="button"
                    onClick={onCancelReply}
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer',
                      padding: '4px 8px', alignSelf: 'flex-start',
                      color: '#8696a0', display: 'flex', alignItems: 'center',
                    }}
                  >
                    <Icon source={XSmallIcon} />
                  </button>
                </div>
              ) : null}

              {/* Row with icons + textarea */}
              <div style={{
                display: 'flex', alignItems: 'center',
                padding: '0 8px',
                minHeight: 48,
              }}>
              {isRecording ? (
                /* ─── Recording UI inside pill (Polaris Button icon-only) ─── */
                <>
                  {/* Delete */}
                  <Button
                    variant="plain"
                    tone="critical"
                    icon={DeleteIcon}
                    onClick={cancelRecording}
                    accessibilityLabel="Descartar grabación"
                  />

                  {/* Play/Pause toggle */}
                  {isPaused ? (
                    isPlayingPreview ? (
                      <Button
                        variant="plain"
                        icon={PauseCircleIcon}
                        onClick={stopPreview}
                        accessibilityLabel="Detener reproducción"
                      />
                    ) : (
                      <Button
                        variant="plain"
                        icon={PlayIcon}
                        onClick={playPreview}
                        accessibilityLabel="Reproducir audio"
                      />
                    )
                  ) : (
                    <Button
                      variant="plain"
                      tone="critical"
                      icon={PauseCircleIcon}
                      onClick={pauseRecording}
                      accessibilityLabel="Pausar grabación"
                    />
                  )}

                  {/* Waveform bars */}
                  <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 2, padding: '0 8px', height: 32 }}>
                    {!isPaused && (
                      <span style={{
                        width: 8, height: 8, borderRadius: '50%', background: '#ea4335',
                        animation: 'wa-pulse 1s infinite', flexShrink: 0,
                      }} />
                    )}
                    {isPaused && isPlayingPreview && (
                      <span style={{
                        width: 8, height: 8, borderRadius: '50%', background: '#00a884',
                        animation: 'wa-pulse 0.6s infinite', flexShrink: 0,
                      }} />
                    )}
                    <div className="wa-waveform" style={{
                      flex: 1, display: 'flex', alignItems: 'center', gap: 1.5, height: 28,
                      overflow: 'hidden', direction: 'rtl',
                    }}>
                      {Array.from({ length: 50 }, (_, i) => {
                        const h = isPaused && !isPlayingPreview
                          ? 4 + Math.sin(i * 0.8) * 10
                          : 4 + Math.random() * 20;
                        return (
                          <div key={i} style={{
                            width: 3, minHeight: 4, height: h, borderRadius: 1.5,
                            background: isPaused ? '#3b4a54' : '#ea4335',
                            opacity: isPaused && !isPlayingPreview ? 0.5 : 0.7,
                            transition: isPaused ? 'none' : 'height 0.2s ease',
                          }} />
                        );
                      })}
                    </div>
                  </div>

                  {/* Timer */}
                  <span style={{
                    fontSize: 13, fontWeight: 500, fontVariantNumeric: 'tabular-nums',
                    color: isPaused ? '#3b4a54' : '#ea4335', marginRight: 4, flexShrink: 0,
                  }}>
                    {Math.floor(recordingTime / 60).toString().padStart(2, '0')}:{(recordingTime % 60).toString().padStart(2, '0')}
                  </span>

                  {/* Mic indicator / Resume */}
                  {isPaused ? (
                    <Button
                      variant="plain"
                      tone="critical"
                      icon={MicrophoneIcon}
                      onClick={resumeRecording}
                      accessibilityLabel="Continuar grabando"
                    />
                  ) : null}
                </>
              ) : (
                /* ─── Normal input UI inside pill ─── */
                <>
                  {/* Note mode indicator — floating label above pill */}
                  {isNoteMode && (
                    <div style={{
                      position: 'absolute', top: -18, left: 16,
                      background: '#FBC02D', color: '#5d4037',
                      fontSize: 11, fontWeight: 600,
                      padding: '1px 8px', borderRadius: '4px 4px 0 0',
                      lineHeight: '16px',
                    }}>
                      Nota interna
                    </div>
                  )}
                  {/* Attach */}
                  <button
                    type="button"
                    data-wa-trigger
                    onClick={() => { setShowAttachMenu((v) => !v); setShowEmojiPicker(false); }}
                    className="wa-icon-btn"
                    style={{
                      transform: showAttachMenu ? 'rotate(135deg)' : 'rotate(0deg)',
                      transition: 'transform 0.2s ease',
                    }}
                    title="Adjuntar archivo"
                  >
                    <span style={{ color: showAttachMenu ? '#00a884' : '#3b4a54' }}><Icon source={AttachmentIcon} /></span>
                  </button>

                  {/* Emoji */}
                  <button
                    type="button"
                    data-wa-trigger
                    onClick={() => { setShowEmojiPicker((v) => !v); setShowAttachMenu(false); }}
                    className="wa-icon-btn"
                    title="Emojis"
                  >
                    <span style={{ color: showEmojiPicker ? '#00a884' : '#3b4a54' }}><Icon source={SmileyHappyIcon} /></span>
                  </button>

                  {/* Text input */}
                  <div style={{ flex: 1, minWidth: 0, position: 'relative', display: 'flex', alignItems: 'center' }}>
                    <div className="wa-textarea-overlay" aria-hidden="true">
                      <WaComposerFormattedPreview text={newMessage} />
                    </div>
                    <textarea
                      ref={textareaRef}
                      className="wa-native-textarea"
                      value={newMessage}
                      onFocus={prewarmWaSend}
                      onPaste={handlePaste}
                      onChange={(e) => {
                        const val = e.target.value;
                        const cursor = e.target.selectionStart ?? val.length;
                        setNewMessage(val);
                        if (val === '/' || (val.startsWith('/') && !val.includes(' '))) {
                          setShowQuickReplies(true);
                          setQrFilter(val.slice(1));
                          setQrSelectedIdx(0);
                        } else {
                          setShowQuickReplies(false);
                        }
                        // @mention autocomplete (solo en modo nota).
                        if (isNoteMode && tenantStaff.length > 0) {
                          const ctx = detectMentionContext(val, cursor);
                          if (ctx) {
                            setMentionQuery(ctx.query);
                            setMentionAtPos(ctx.atPos);
                            setMentionSelectedIdx(0);
                          } else {
                            setMentionQuery(null);
                          }
                        } else if (mentionQuery !== null) {
                          setMentionQuery(null);
                        }
                        setShowEmojiPicker(false);
                        setShowAttachMenu(false);
                        if (onTyping && val.trim() && Date.now() - lastTypingEmitRef.current > 3000) {
                          lastTypingEmitRef.current = Date.now();
                          onTyping();
                        }
                      }}
                      placeholder={isNoteMode ? 'Nota interna — usa @ para mencionar a un compañero' : 'Escribe un mensaje'}
                      autoComplete="off"
                      rows={1}
                      onKeyDown={(e) => {
                        // @mention dropdown tiene prioridad sobre quick replies cuando está activo.
                        if (mentionQuery !== null && filteredMentionStaff.length > 0) {
                          if (e.key === 'ArrowDown') { e.preventDefault(); setMentionSelectedIdx((prev) => Math.min(prev + 1, filteredMentionStaff.length - 1)); return; }
                          if (e.key === 'ArrowUp') { e.preventDefault(); setMentionSelectedIdx((prev) => Math.max(prev - 1, 0)); return; }
                          if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); insertMention(filteredMentionStaff[mentionSelectedIdx]); return; }
                          if (e.key === 'Escape') { e.preventDefault(); setMentionQuery(null); return; }
                        }
                        if (showQuickReplies && filteredQR.length > 0) {
                          if (e.key === 'ArrowDown') { e.preventDefault(); setQrSelectedIdx((prev) => Math.min(prev + 1, filteredQR.length - 1)); return; }
                          if (e.key === 'ArrowUp') { e.preventDefault(); setQrSelectedIdx((prev) => Math.max(prev - 1, 0)); return; }
                          if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); setNewMessage(filteredQR[qrSelectedIdx].body); setShowQuickReplies(false); setQrFilter(''); return; }
                          if (e.key === 'Escape') { setShowQuickReplies(false); return; }
                        }
                        if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey) { e.preventDefault(); handleSend(); }
                        if (e.key === 'Enter' && e.ctrlKey) {
                          e.preventDefault();
                          setNewMessage((prev) => prev + '\n');
                        }
                      }}
                    />
                  </div>
                  {/* @mention dropdown — renderizado via portal para escapar el
                      stacking context del composer (el chat thread tiene un
                      backdrop que tapaba el dropdown si vivía dentro del flow). */}
                  {mentionQuery !== null && filteredMentionStaff.length > 0 && mentionAnchor && createPortal(
                    <div
                      data-wa-popup
                      style={{
                        position: 'fixed',
                        left: mentionAnchor.left,
                        bottom: mentionAnchor.bottom,
                        background: '#fff',
                        border: '1px solid rgba(0,0,0,0.08)',
                        borderRadius: 8,
                        boxShadow: '0 6px 20px rgba(0,0,0,0.18)',
                        padding: 4,
                        minWidth: 240,
                        maxWidth: 320,
                        zIndex: 99999,
                      }}
                    >
                      <div style={{ padding: '4px 10px', fontSize: 11, color: '#667781', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                        Mencionar a
                      </div>
                      {filteredMentionStaff.map((member, idx) => (
                        <button
                          key={member.id}
                          type="button"
                          onMouseDown={(e) => { e.preventDefault(); insertMention(member); }}
                          onMouseEnter={() => setMentionSelectedIdx(idx)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            width: '100%',
                            gap: 8,
                            padding: '8px 10px',
                            borderRadius: 6,
                            border: 'none',
                            background: idx === mentionSelectedIdx ? '#F0FDF4' : 'transparent',
                            cursor: 'pointer',
                            textAlign: 'left',
                            fontSize: 13,
                            color: '#111B21',
                          }}
                        >
                          <span style={{
                            width: 26, height: 26, borderRadius: '50%',
                            background: '#25D366', color: '#fff',
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 11, fontWeight: 600, flexShrink: 0,
                          }}>
                            {member.name.split(' ').map((p) => p[0]).slice(0, 2).join('').toUpperCase()}
                          </span>
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {member.name}
                          </span>
                        </button>
                      ))}
                    </div>,
                    document.body,
                  )}

                  {/* Note toggle */}
                  <button
                    type="button"
                    onClick={() => setIsNoteMode((v) => !v)}
                    className="wa-icon-btn"
                    title={isNoteMode ? 'Modo nota interna (click para mensaje normal)' : 'Escribir nota interna'}
                  >
                    <span style={{ color: isNoteMode ? '#F9A825' : '#3b4a54' }}><Icon source={NoteIcon} /></span>
                  </button>

                  {/* KB search */}
                  <button
                    type="button"
                    onClick={() => { setShowKbSearch((v) => !v); setShowEmojiPicker(false); setShowAttachMenu(false); }}
                    className="wa-icon-btn"
                    title="Buscar en base de conocimiento"
                  >
                    <span style={{ color: showKbSearch ? '#25D366' : '#3b4a54' }}><Icon source={BookOpenIcon} /></span>
                  </button>

                  {/* IA tools — reformular / corregir / traducir */}
                  <WaComposerAiTools
                    text={newMessage}
                    onApply={(newText) => setNewMessage(newText)}
                  />

                  {/* Mic — inside pill, only when no text */}
                  {!newMessage.trim() && (
                    <button
                      type="button"
                      onClick={() => void startRecording()}
                      className="wa-icon-btn"
                      title="Grabar audio"
                    >
                      <span style={{ color: '#3b4a54' }}><Icon source={MicrophoneIcon} /></span>
                    </button>
                  )}

                  {/* Schedule — only when there's text */}
                  {newMessage.trim() && onScheduleMessage ? (
                    <Popover
                      active={showSchedulePicker}
                      activator={
                        <button
                          type="button"
                          data-wa-trigger
                          onClick={() => { setShowSchedulePicker((v) => !v); setShowEmojiPicker(false); setShowAttachMenu(false); }}
                          className="wa-icon-btn"
                          title="Programar envío"
                        >
                          <span style={{ color: showSchedulePicker ? '#00a884' : '#3b4a54' }}><Icon source={ClockIcon} /></span>
                        </button>
                      }
                      onClose={() => { setShowSchedulePicker(false); }}
                      preferredPosition="above"
                      preferredAlignment="right"
                    >
                      <Box padding="400" minWidth="300px">
                        <BlockStack gap="300">
                          <Text as="p" variant="headingSm">Programar mensaje</Text>
                          <DatePicker
                            month={scheduleMonth}
                            year={scheduleYear}
                            selected={scheduleSelectedDate ? { start: scheduleSelectedDate, end: scheduleSelectedDate } : undefined}
                            onMonthChange={(month, year) => { setScheduleMonth(month); setScheduleYear(year); }}
                            onChange={({ start }) => setScheduleSelectedDate(start)}
                            disableDatesBefore={new Date()}
                          />
                          <TextField
                            label="Hora de envío"
                            type="time"
                            value={scheduleTime}
                            onChange={setScheduleTime}
                            autoComplete="off"
                          />
                          <InlineStack gap="200" align="end">
                            <Button onClick={() => { setShowSchedulePicker(false); setScheduleSelectedDate(null); setScheduleTime(''); }}>
                              Cancelar
                            </Button>
                            <Button
                              variant="primary"
                              disabled={!scheduleSelectedDate || !scheduleTime || !newMessage.trim()}
                              onClick={() => {
                                if (onScheduleMessage && scheduleSelectedDate && scheduleTime && newMessage.trim()) {
                                  const [hh, mm] = scheduleTime.split(':').map(Number);
                                  const dt = new Date(scheduleSelectedDate);
                                  dt.setHours(hh, mm, 0, 0);
                                  if (dt <= new Date()) return;
                                  onScheduleMessage(newMessage.trim(), dt.toISOString());
                                  setNewMessage(''); setScheduleSelectedDate(null); setScheduleTime(''); setShowSchedulePicker(false);
                                }
                              }}
                            >
                              Programar
                            </Button>
                          </InlineStack>
                        </BlockStack>
                      </Box>
                    </Popover>
                  ) : null}
                </>
              )}
              </div>{/* end row with icons + textarea */}
            </div>

            {/* Send button — visible when text OR recording */}
            {(newMessage.trim() || isRecording || pendingMedia || pendingPasteFile) ? (
              <button
                type="button"
                disabled={sending}
                onClick={isRecording ? sendRecording : handleSend}
                style={{
                  width: 42, height: 42, borderRadius: '50%', border: 'none',
                  background: '#00a884', cursor: sending ? 'wait' : 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0, opacity: sending ? 0.5 : 1,
                  transition: 'opacity 0.15s',
                  boxShadow: '0 1px 6px rgba(0,168,132,0.25)',
                }}
                title={isRecording ? 'Enviar audio (↵)' : 'Enviar (↵)'}
                aria-label={isRecording ? 'Enviar audio. Atajo: Enter' : 'Enviar mensaje. Atajo: Enter'}
              >
                {sending ? <Spinner size="small" /> : (
                  <span style={{ color: '#fff' }}><Icon source={SendIcon} /></span>
                )}
              </button>
            ) : null}
          </div>
      </div>

      {/* Styles: pulse animation, icon buttons, textarea */}
      <style>{`
        @keyframes wa-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }
        /* Shared icon button style */
        .wa-icon-btn {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          border: none;
          background: transparent;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          padding: 0;
          transition: background 0.15s;
        }
        .wa-icon-btn .Polaris-Icon {
          transform: translateZ(0);
          -webkit-font-smoothing: antialiased;
        }
        .wa-icon-btn:hover {
          background: rgba(0,0,0,0.05);
        }
        /* Native textarea inside pill — text is transparent, overlay renders formatted text */
        .wa-native-textarea {
          position: relative;
          z-index: 1;
          width: 100%;
          border: none;
          outline: none;
          background: transparent;
          font-family: inherit;
          font-size: 15px;
          line-height: 20px;
          height: 20px;
          max-height: 140px;
          padding: 0 4px;
          margin: 0;
          resize: none;
          overflow-y: hidden;
          color: transparent;
          caret-color: var(--p-color-text);
          scrollbar-width: none;
        }
        .wa-native-textarea::-webkit-scrollbar {
          display: none;
        }
        .wa-native-textarea::placeholder {
          color: #999;
        }
        .wa-native-textarea::selection {
          background: rgba(0,168,132,0.25);
          color: transparent;
        }
        /* Overlay that renders text with WhatsApp-style formatting,
           aligned pixel-perfect with the textarea underneath. */
        .wa-textarea-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          pointer-events: none;
          z-index: 0;
          font-family: inherit;
          font-size: 15px;
          line-height: 20px;
          padding: 0 4px;
          margin: 0;
          color: var(--p-color-text);
          white-space: pre-wrap;
          word-break: break-word;
          overflow: hidden;
        }
      `}</style>
    </>
  );
}
